import React, { useEffect, useState, useMemo } from 'react';
import api from '../../api/api';
import { Card, Badge } from '../../components/ui';
import TableToolbar from '../../components/stock/TableToolbar';

const toolbarColumns = [
  { label: 'Article', accessor: 'name' },
  { label: 'SKU', accessor: 'sku' },
  { label: 'Réf.', accessor: 'reference' },
  { label: 'Catégorie', accessor: 'category' },
  { label: 'Unité', accessor: 'unit' },
  { label: 'Qté', accessor: 'qty' },
  { label: 'Prix achat', accessor: 'purchasePrice' },
  { label: 'Prix vente', accessor: 'sellingPrice' },
  { label: 'Valeur', accessor: 'lineValue' },
  { label: 'Statut', accessor: 'status' },
];

export default function StockInventory() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [groupByCategory, setGroupByCategory] = useState(false);
  const [search, setSearch] = useState('');

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const params = { limit: 200 };
        if (search) params.q = search;
        const res = await api.get('/stock/items', { params });
        if (res.data.ok) setItems(res.data.items.filter((i) => i.status === 'active'));
      } catch (err) {
        console.error('Erreur chargement inventaire:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, [search]);

  const totalValue = useMemo(
    () => items.reduce((sum, i) => sum + (i.currentQuantity || 0) * (i.purchasePrice || 0), 0),
    [items],
  );

  const totalSellingValue = useMemo(
    () => items.reduce((sum, i) => sum + (i.currentQuantity || 0) * (i.sellingPrice || 0), 0),
    [items],
  );

  const totalQty = useMemo(
    () => items.reduce((sum, i) => sum + (i.currentQuantity || 0), 0),
    [items],
  );

  const grouped = useMemo(() => {
    if (!groupByCategory) return null;
    const map = {};
    items.forEach((i) => {
      const cat = i.categoryId?.name || 'Sans catégorie';
      if (!map[cat]) map[cat] = { items: [], subtotal: 0, subQty: 0 };
      map[cat].items.push(i);
      map[cat].subtotal += (i.currentQuantity || 0) * (i.purchasePrice || 0);
      map[cat].subQty += (i.currentQuantity || 0);
    });
    return map;
  }, [items, groupByCategory]);

  const exportData = useMemo(() => items.map((i) => ({
    name: i.name,
    sku: i.sku || '-',
    reference: i.reference || '-',
    category: i.categoryId?.name || '-',
    unit: i.unit,
    qty: i.currentQuantity,
    purchasePrice: i.purchasePrice,
    sellingPrice: i.sellingPrice,
    lineValue: (i.currentQuantity || 0) * (i.purchasePrice || 0),
    status: (i.currentQuantity <= 0) ? 'Rupture' : (i.minQuantity > 0 && i.currentQuantity <= i.minQuantity) ? 'Stock bas' : 'OK',
  })), [items]);

  if (loading) return <p className="text-gray-500 p-6">Chargement...</p>;

  const renderRow = (item) => {
    const lineValue = (item.currentQuantity || 0) * (item.purchasePrice || 0);
    const isLow = item.minQuantity > 0 && item.currentQuantity <= item.minQuantity;
    const isOut = item.currentQuantity <= 0;
    return (
      <tr key={item._id} className="border-t hover:bg-gray-50">
        <td className="px-4 py-2 font-medium">{item.name}</td>
        <td className="px-4 py-2 text-gray-600">{item.sku || '-'}</td>
        <td className="px-4 py-2 text-gray-600">{item.reference || '-'}</td>
        <td className="px-4 py-2">{item.categoryId?.name || '-'}</td>
        <td className="px-4 py-2">{item.unit}</td>
        <td className={`px-4 py-2 text-right font-bold ${isOut ? 'text-red-600' : isLow ? 'text-yellow-600' : ''}`}>
          {item.currentQuantity}
        </td>
        <td className="px-4 py-2 text-right">
          {item.purchasePrice.toLocaleString('fr-TN', { style: 'currency', currency: 'TND' })}
        </td>
        <td className="px-4 py-2 text-right">
          {item.sellingPrice.toLocaleString('fr-TN', { style: 'currency', currency: 'TND' })}
        </td>
        <td className="px-4 py-2 text-right font-semibold">
          {lineValue.toLocaleString('fr-TN', { style: 'currency', currency: 'TND' })}
        </td>
        <td className="px-4 py-2 text-center">
          <Badge variant={isOut ? 'danger' : isLow ? 'warning' : 'success'}>
            {isOut ? 'Rupture' : isLow ? 'Stock bas' : 'OK'}
          </Badge>
        </td>
      </tr>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold text-gray-900">État du stock</h1>
          <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
            <input
              type="checkbox"
              checked={groupByCategory}
              onChange={(e) => setGroupByCategory(e.target.checked)}
              className="rounded"
            />
            Grouper par catégorie
          </label>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <Card className="p-4">
            <p className="text-sm text-gray-500 mb-1">Articles actifs</p>
            <p className="text-3xl font-bold text-gray-900">{items.length}</p>
          </Card>
          <Card className="p-4">
            <p className="text-sm text-gray-500 mb-1">Quantité totale</p>
            <p className="text-3xl font-bold text-gray-900">{totalQty}</p>
          </Card>
          <Card className="p-4">
            <p className="text-sm text-gray-500 mb-1">Valeur au prix d'achat</p>
            <p className="text-3xl font-bold text-green-600">
              {totalValue.toLocaleString('fr-TN', { style: 'currency', currency: 'TND' })}
            </p>
          </Card>
          <Card className="p-4">
            <p className="text-sm text-gray-500 mb-1">Valeur au prix de vente</p>
            <p className="text-3xl font-bold text-blue-600">
              {totalSellingValue.toLocaleString('fr-TN', { style: 'currency', currency: 'TND' })}
            </p>
          </Card>
        </div>

        <TableToolbar
          title="État du stock"
          search={search}
          onSearchChange={setSearch}
          searchPlaceholder="Rechercher par nom, SKU ou référence..."
          columns={toolbarColumns}
          data={exportData}
          filename="etat_stock"
        />

        <Card>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left px-4 py-2">Article</th>
                  <th className="text-left px-4 py-2">SKU</th>
                  <th className="text-left px-4 py-2">Réf.</th>
                  <th className="text-left px-4 py-2">Catégorie</th>
                  <th className="text-left px-4 py-2">Unité</th>
                  <th className="text-right px-4 py-2">Qté</th>
                  <th className="text-right px-4 py-2">Prix achat</th>
                  <th className="text-right px-4 py-2">Prix vente</th>
                  <th className="text-right px-4 py-2">Valeur</th>
                  <th className="text-center px-4 py-2">Statut</th>
                </tr>
              </thead>
              <tbody>
                {items.length === 0 && (
                  <tr><td colSpan={10} className="text-center py-4 text-gray-500">Aucun article en stock</td></tr>
                )}
                {groupByCategory
                  ? Object.entries(grouped || {}).map(([cat, g]) => (
                      <React.Fragment key={cat}>
                        <tr className="bg-gray-100 font-semibold">
                          <td colSpan={5} className="px-4 py-2 text-gray-700">{cat}</td>
                          <td className="px-4 py-2 text-right text-gray-700">{g.subQty}</td>
                          <td colSpan={2}></td>
                          <td className="px-4 py-2 text-right text-gray-700">
                            {g.subtotal.toLocaleString('fr-TN', { style: 'currency', currency: 'TND' })}
                          </td>
                          <td></td>
                        </tr>
                        {g.items.map(renderRow)}
                      </React.Fragment>
                    ))
                  : items.map(renderRow)}
              </tbody>
              <tfoot className="bg-gray-50 font-semibold">
                <tr>
                  <td colSpan={5} className="px-4 py-2 text-gray-700">Total</td>
                  <td className="px-4 py-2 text-right">{totalQty}</td>
                  <td colSpan={2}></td>
                  <td className="px-4 py-2 text-right">
                    {totalValue.toLocaleString('fr-TN', { style: 'currency', currency: 'TND' })}
                  </td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </Card>
      </div>
    </div>
  );
}